import { useEffect, useState } from "react";
import { Save, Send, Eye, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../lib/api";
import { Card, Title, Field, Toggle, act } from "./kit";

function Log({ log }) {
  return (
    <Card className="mt-4" data-testid="emails-log">
      <p className="font-display text-lg mb-3">Historie odeslaných e-mailů</p>
      {log.length === 0 ? <p className="text-sm text-[var(--muted)]">Zatím nebyl odeslán žádný e-mail.</p> : (
        <div className="divide-y divide-[#f1e4ea] text-sm">
          {log.map((l, i) => (
            <div key={i} className="py-2 flex flex-wrap gap-x-4 gap-y-1 items-center">
              {l.ok ? <CheckCircle2 className="w-4 h-4 text-[#2b8f8a]" /> : <XCircle className="w-4 h-4 text-[var(--pink-dark)]" />}
              <span className="text-[var(--muted)] w-36">{new Date(l.at).toLocaleString("cs-CZ")}</span>
              <span className="font-semibold flex-1 min-w-[200px]">{l.subject}</span>
              <span className="text-[var(--muted)]">{l.to}</span>
              {!l.ok && l.error && <span className="text-xs text-[var(--pink-dark)] w-full pl-8">{l.error}</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Emails() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("order_received");
  const [t, setT] = useState(null);
  const [prev, setPrev] = useState(null);
  const load = () => adminApi.get("/emails").then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (data) {
      setT(data.templates[sel]);
      setPrev(null);
    }
  }, [sel, data]);
  if (!data || !t) return null;
  const save = () => act(async () => {
    await adminApi.put(`/emails/${sel}`, t);
    await load();
  }, "E-mail uložen");
  const preview = async () => {
    try {
      setPrev((await adminApi.post(`/emails/${sel}/preview`, t)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendTest = async () => {
    try {
      await adminApi.put(`/emails/${sel}`, t);
      const { data: r } = await adminApi.post(`/emails/${sel}/test`);
      (r.ok ? toast.success : toast.error)(r.message);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="admin-emails">
      <Title right={<button onClick={save} className="btn-pink" data-testid="emails-save-btn"><Save className="w-4 h-4" /> Uložit</button>}>Automatické e-maily</Title>
      <p className="text-sm text-[var(--muted)] -mt-2 mb-5">E-maily se odesílají automaticky přes Resend. Upravte texty, vypněte, co nechcete posílat, a ukázku si pošlete na svou adresu pro oznámení.</p>
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <Card className="!p-3">
          {Object.entries(data.templates).map(([k, v]) => (
            <button key={k} onClick={() => setSel(k)} data-testid={`emails-tpl-${k}`} className={`w-full text-left px-3 py-2.5 rounded-2xl text-sm transition-colors flex items-center gap-2 ${sel === k ? "bg-[var(--pink-soft)]" : "hover:bg-[var(--blush)]"}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${v.enabled ? "bg-[#2b8f8a]" : "bg-gray-300"}`} />
              <span className="flex-1"><b className="block">{v.label}</b><span className="text-xs text-[var(--muted)]">komu: {v.who}</span></span>
            </button>
          ))}
        </Card>
        <Card data-testid="emails-editor">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <p className="font-display text-lg">{t.label}</p>
            {t.locked ? <span className="text-xs flex items-center gap-1 text-[var(--muted)]"><Lock className="w-3 h-3" /> Odesílá se vždy (bezpečnost účtu)</span> : <Toggle checked={t.enabled} onChange={(v) => setT({ ...t, enabled: v })} label="Odesílat automaticky" testid="emails-enabled-toggle" />}
          </div>
          <div className="space-y-3">
            <Field label="Předmět"><input className="field" value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} data-testid="emails-subject" /></Field>
            <Field label="Nadpis v e-mailu"><input className="field" value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} data-testid="emails-title" /></Field>
            <Field label="Text (prázdný řádek = nový odstavec)"><textarea rows={6} className="field" value={t.intro} onChange={(e) => setT({ ...t, intro: e.target.value })} data-testid="emails-intro" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              {sel !== "contact_owner" && <Field label="Text tlačítka"><input className="field" value={t.button} onChange={(e) => setT({ ...t, button: e.target.value })} data-testid="emails-button" /></Field>}
              {sel === "payment_reminder" && <Field label="Poslat po (dnech bez platby)"><input type="number" min={1} max={30} className="field" value={t.days} onChange={(e) => setT({ ...t, days: e.target.value })} data-testid="emails-days" /></Field>}
            </div>
            <p className="text-xs text-[var(--muted)]">Proměnné: <b>{t.vars}</b>. Seznam položek, platební údaje a odkazy se doplní automaticky.</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={preview} className="btn-ghost !py-2" data-testid="emails-preview-btn"><Eye className="w-4 h-4" /> Náhled</button>
            <button onClick={sendTest} className="btn-ghost !py-2" data-testid="emails-test-btn"><Send className="w-4 h-4" /> Poslat ukázku na můj e-mail</button>
          </div>
          {prev && (
            <div className="mt-4" data-testid="emails-preview">
              <p className="text-sm mb-2"><b>Předmět:</b> {prev.subject}</p>
              <iframe title="náhled" srcDoc={prev.html} sandbox="" className="w-full h-[520px] rounded-2xl border border-[#f1e4ea] bg-white" />
            </div>
          )}
        </Card>
      </div>
      <Log log={data.log} />
    </div>
  );
}
